import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Modal from '../components/Modal';
import { ToastContext } from '../App';
import { formatDate } from '../utils/helpers';
import { CASE_STAGES, CASE_CATEGORIES, CASE_CATEGORY_MAP, CASE_STAGE_MAP, CASE_ROLES, Chip } from '../utils/cases';
import { LEAD_SOURCES } from '../utils/sources';

// Развёрнутые анкеты: server/scripts/gen_*_anketa.py → client/public/materials/<file>.docx/.pdf
const DETAILED_FORMS = [
  ['anketa-razdel', 'Раздел имущества'],
  ['anketa-dtp', 'ДТП'],
  ['anketa-nedv', 'Недвижимость и земля без документов'],
  ['anketa-trud', 'Трудовой спор (без оформления)'],
  ['anketa-blokirovka', 'Блокировка карт (161-ФЗ, 115-ФЗ)'],
  ['anketa-kollektory', 'Взыскатели и коллекторы (угрозы, утечка данных)'],
  ['pamyatka-kollektory', '↳ Памятка клиенту: давят из-за долга'],
];

const money = n => Number(n || 0).toLocaleString('ru-RU') + ' ₽';
const EMPTY = { client_name: '', phone: '', category: 'family', title: '', role: 'none', source: '', consultation_date: '', consultation_time: '', fee: '' };

export default function CasesPage() {
  const navigate = useNavigate();
  const showToast = useContext(ToastContext);
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [view, setView] = useState('kanban');
  const [showClosed, setShowClosed] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get('/cases', { params: { search, category } }).then(r => setCases(r.data)).catch(() => {});
  useEffect(() => { load(); }, [search, category]);

  const create = async () => {
    if (!form.client_name.trim()) { showToast('Укажите клиента', 'error'); return; }
    if (!form.source) { showToast('Укажите источник: откуда человек о нас узнал (не знаете — «Другое»)', 'error'); return; }
    try {
      const { data } = await api.post('/cases', form);
      setForm(null);
      showToast('Дело создано');
      navigate(`/cases/${data.id}`);
    } catch (e) { showToast(e.response?.data?.error || 'Ошибка', 'error'); }
  };

  const stages = CASE_STAGES.filter(s => showClosed || !['completed', 'refused'].includes(s.key));
  const grouped = Object.fromEntries(CASE_STAGES.map(s => [s.key, []]));
  cases.forEach(c => { (grouped[c.stage] || grouped.consultation).push(c); });
  const visible = cases.filter(c => showClosed || !['completed', 'refused'].includes(c.stage));

  const Card = ({ c }) => {
    const cat = CASE_CATEGORY_MAP[c.category] || CASE_CATEGORY_MAP.other;
    return (
      <div className="kanban-card" onClick={() => navigate(`/cases/${c.id}`)}>
        <h4>{c.client_name}{c.paused && <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309', fontWeight: 600 }}>⏸ пауза</span>}</h4>
        <div style={{ marginBottom: 4 }}><Chip color={cat.color}>{cat.label}</Chip></div>
        {c.title && <div className="meta" style={{ marginBottom: 3 }}>{c.title}</div>}
        {c.stage === 'consultation' && c.consultation_date && (
          <div className="meta">Консультация: {formatDate(c.consultation_date)}{c.consultation_time ? ` ${c.consultation_time}` : ''}</div>
        )}
        {c.next_hearing && <div className="meta" style={{ color: 'var(--accent)', fontWeight: 600 }}>Заседание: {formatDate(c.next_hearing)}</div>}
        {Number(c.fee) > 0 && <div className="meta">Оплачено {money(c.paid_total)} из {money(c.fee)}</div>}
        {Number(c.overdue_tasks) > 0 && <div className="meta" style={{ color: 'var(--red)', fontWeight: 600 }}>Просрочено задач: {c.overdue_tasks}</div>}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Гражданские дела</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <a className="btn btn-secondary btn-sm" href="/materials/anketa-konsultaciya.pdf" target="_blank" rel="noreferrer" title="Бланк анкеты консультации (PDF для печати). Word — /materials/anketa-konsultaciya.docx">📋 Анкета</a>
          <select className="form-select" style={{ width: 'auto' }} value="" title="Развёрнутые анкеты под конкретные ситуации (PDF; Word — тот же адрес с .docx)"
            onChange={e => { if (e.target.value) window.open(`/materials/${e.target.value}.pdf`, '_blank', 'noopener'); }}>
            <option value="">📋 Подробные анкеты…</option>
            {DETAILED_FORMS.map(([file, label]) => <option key={file} value={file}>{label}</option>)}
          </select>
          <div className="search-bar"><span>{'\u{1F50D}'}</span><input placeholder="Клиент, телефон, № дела, сторона, номер дела в CRM" value={search} onChange={e => setSearch(e.target.value)} /></div>
          <select className="form-select" style={{ width: 'auto' }} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="all">Все категории</option>
            {CASE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="checkbox" checked={showClosed} onChange={() => setShowClosed(!showClosed)} /> Завершённые и отказы
          </label>
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            <button className={`btn btn-sm ${view === 'kanban' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('kanban')} style={{ borderRadius: '5px 0 0 5px' }}>Канбан</button>
            <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('list')} style={{ borderRadius: '0 5px 5px 0' }}>Список</button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setForm({ ...EMPTY })}>+ Дело</button>
        </div>
      </div>

      {view === 'kanban' ? (
        <div className="kanban-board">
          {stages.map(s => (
            <div className="kanban-column" key={s.key}>
              <div className="kanban-header">
                <span style={{ width: 8, height: 8, borderRadius: 4, background: s.color, display: 'inline-block' }} />
                <span>{s.label}</span><span className="count">{grouped[s.key].length}</span>
              </div>
              <div className="kanban-cards">
                {grouped[s.key].map(c => <Card key={c.id} c={c} />)}
                {!grouped[s.key].length && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Пусто</div>}
                {s.key === 'consultation' && <div style={{ textAlign: 'center', padding: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setForm({ ...EMPTY })}>+ Дело</button></div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <thead><tr><th>Клиент</th><th>Категория</th><th>Суть</th><th>Стадия</th><th>Суд / № дела</th><th>Заседание</th><th>Оплата</th></tr></thead>
            <tbody>
              {visible.map(c => {
                const cat = CASE_CATEGORY_MAP[c.category] || CASE_CATEGORY_MAP.other;
                const st = CASE_STAGE_MAP[c.stage] || CASE_STAGE_MAP.consultation;
                return (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/cases/${c.id}`)}>
                    <td style={{ fontWeight: 600 }}>{c.client_name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.phone}</div></td>
                    <td><Chip color={cat.color}>{cat.label}</Chip></td>
                    <td style={{ maxWidth: 260 }}>{c.title || '—'}</td>
                    <td><Chip color={st.color}>{st.label}</Chip>{c.paused && <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginTop: 2 }}>⏸ пауза</div>}</td>
                    <td>{c.court || '—'}{c.case_number ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>№ {c.case_number}</div> : null}</td>
                    <td>{c.next_hearing ? formatDate(c.next_hearing) : '—'}</td>
                    <td>{Number(c.fee) > 0 ? `${money(c.paid_total)} / ${money(c.fee)}` : '—'}</td>
                  </tr>
                );
              })}
              {!visible.length && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Дел пока нет</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <Modal title="Новое дело" onClose={() => setForm(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setForm(null)}>Отмена</button><button className="btn btn-primary" onClick={create}>Создать</button></>}>
          <div className="form-row">
            <div className="form-group"><label>Клиент (ФИО) *</label><input className="form-input" autoFocus value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} /></div>
            <div className="form-group"><label>Телефон</label><input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Категория</label>
              <select className="form-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CASE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Роль клиента</label>
              <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {CASE_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label>Суть вопроса</label><input className="form-input" placeholder="Например: развод и раздел квартиры" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
          <div className="form-row">
            <div className="form-group"><label>Дата консультации</label><input className="form-input" type="date" value={form.consultation_date} onChange={e => setForm({ ...form, consultation_date: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Источник *</label><select className="form-select" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}><option value="">Не указан</option>{LEAD_SOURCES.map(x => <option key={x}>{x}</option>)}</select></div>
            <div className="form-group"><label>Гонорар, ₽</label><input className="form-input" type="number" value={form.fee} onChange={e => setForm({ ...form, fee: e.target.value })} /></div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Если указать дату консультации — появится задача в разделе «Задачи».</div>
        </Modal>
      )}
    </div>
  );
}
